import { CommonModule } from '@angular/common';
import { Component, computed, Inject, Optional, Signal, signal, WritableSignal } from '@angular/core';
import { SvgComponent } from '../../svg/svg.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { SettingsService } from '@app/_services/settings.service';
import { PolicyType } from '@app/_types/generic.type';
import { CustomBottomSheetRef } from '@app/_services/custom-bottom-sheet.service';
import { MAT_BOTTOM_SHEET_DATA } from '@angular/material/bottom-sheet';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BRAND_CONFIG } from '@app/_generated/brand.gen';

@Component({
  selector: 'app-policy-dialog',
  imports: [
    CommonModule,
    SvgComponent
  ],
  templateUrl: './policy-dialog.component.html',
  styleUrl: './policy-dialog.component.scss'
})

export class PolicyDialogComponent {

  isLoading: WritableSignal<boolean> = signal<boolean>(true);
  policyType: WritableSignal<PolicyType> = signal<PolicyType>('PRIVACY_POLICY');
  config: Signal<{ [key in PolicyType]: { title: string, url: string } }> = computed(() => {
    return {
      PRIVACY_POLICY: {
        title: this.ss.t('common_privacyPolicy'),
        url: `${BRAND_CONFIG.legal.privacyPolicy}?embed=true`
      },
      TERMS_OF_USE: {
        title: this.ss.t('account_legalInformationTermsOfUse'),
        url: `${BRAND_CONFIG.legal.termsOfUse}?embed=true`
      },
      COOKIE_POLICY: {
        title: this.ss.t('cookiesPolicy_title'),
        url: `${BRAND_CONFIG.legal.cookiePolicy}?embed=true`
      },
      END_USER_LICENSE_AGREEMENT: {
        title: this.ss.t('account_legalInformationEndUserLicenseAgreement'),
        url: `${BRAND_CONFIG.legal.eula}?embed=true`
      },
      DISCLAIMER: {
        title: this.ss.t('account_legalInformationDisclaimer'),
        url: `${BRAND_CONFIG.legal.disclaimer}?embed=true`
      },
      ACCEPTABLE_USE_POLICY: {
        title: this.ss.t('account_legalInformationAcceptableUsePolicy'),
        url: `${BRAND_CONFIG.legal.acceptableUsePolicy}?embed=true`
      }
    }
  });

  policyTitle: Signal<string> = computed(() => {
    return this.config()[this.policyType()].title;
  });

  policyUrl: Signal<SafeResourceUrl> = computed(() => {
    const baseUrl = this.config()[this.policyType()].url;
    const mode = this.ss.mode();
    const urlWithMode = `${baseUrl}&mode=${mode}`;
    return this.sanitizer.bypassSecurityTrustResourceUrl(urlWithMode);
  });

  public data: { type: PolicyType };
  constructor(
    public ss: SettingsService,
    private sanitizer: DomSanitizer,
    @Optional() public dialogRef: MatDialogRef<PolicyDialogComponent> | null,
    @Optional() public bottomSheetRef: CustomBottomSheetRef<PolicyDialogComponent> | null,
    @Optional() @Inject(MAT_DIALOG_DATA) dialogData: { type: PolicyType } | null,
    @Optional() @Inject(MAT_BOTTOM_SHEET_DATA) bottomSheetData: { type: PolicyType } | null,
  ) {
    this.data = (dialogData || bottomSheetData)!;
    this.policyType.set(this.data.type);

    dialogRef?.backdropClick().pipe().subscribe(async () => {
      this.closeDialog();
    });

    bottomSheetRef?.backdropClick().pipe().subscribe(async () => {
      this.closeDialog();
    });
  }

  onIframeLoad(): void {
    this.isLoading.set(false);
  }

  closeDialog() {
    this.close();
  }

  private close(result?: any): void {
    if (this.dialogRef) {
      this.dialogRef.close(result);
    } else if (this.bottomSheetRef) {
      this.bottomSheetRef.dismiss(result);
    }
  }

}
